<script lang="ts" setup>import { computed as _computed, ref as _ref } from 'vue';

import { TransitionRoot } from '@headlessui/vue'
import closeIcon from '@consumer/assets/icons/close.svg?svgicon'

defineOptions({
  inheritAttrs: false,
})

const TYPE_COLORS = {
  info: '#00A2F1', // v2-active
  success: '#0F9D58', // green-base
  warning: '#ED7D19', // orange-900
  error: '#CF142B', // red-base
}

withDefaults(defineProps<{
  title?: string
  text?: string
  type?: keyof typeof TYPE_COLORS
  color?: string
  backgroundColor?: string
  hideable?: boolean
  autoCloseAfter?: number | false // In milliseconds
  noBorder?: boolean
}>(), { type: 'info',hideable: true,noBorder: false, })

const slots = useSlots()

const typeColor = _computed(() => TYPE_COLORS[__props.type || 'info'])

const bgColor = computed(() => __props.backgroundColor ?? typeColor.value)

const emit = defineEmits<{
  'update:text': [value: string | undefined]
  close: []
}>()

let isVisible = _ref(true)

function closeMessage () {
  isVisible.value = false
  emit('close')
}

watch(
  () => [__props.text, slots.default],
  () => isVisible.value = true,
  { immediate: true },
)

onMounted(() => {
  if (__props.autoCloseAfter) {
    setTimeout(() => {
      emit('update:text', undefined)
      closeMessage()
    }, __props.autoCloseAfter)
  }
})
</script>

<template>
  <TransitionRoot
    :show="isVisible"
    enter="ease-in-out duration-300 transform"
    enterFrom="opacity-0 -translate-y-full"
    enterTo="opacity-100 translate-y-0"
    leave="ease-in-out duration-200 transform"
    leaveFrom="opacity-100 translate-y-0"
    leaveTo="opacity-0 -translate-y-full"
    class="bg-white rounded-md"
  >
    <div
      class="
        flex items-start justify-between relative g-message
        py-4 px-6 w-full rounded-md
      "
      :class="noBorder ? '' : 'border-1 border-solid'"
      :style="{
        color: `color-mix(in oklab, ${bgColor}, black 10%)`,
        backgroundColor: backgroundColor ?? `color-mix(in oklab, ${bgColor}, white 90%)`,
      }"
      v-bind="$attrs"
    >
      <div :class="['w-full', { 'mr-4': hideable }]">
        <div v-if="title" class="font-semibold mb-3">{{ title }}</div>
        <slot>
          <span class="whitespace-pre-wrap" v-text="text"/>
        </slot>
      </div>
      <button v-if="hideable" class="m-1 absolute top-4 right-4" name="close" @click="closeMessage">
        <GIcon :svg="closeIcon" :color="color" size="12px"/>
      </button>
    </div>
  </TransitionRoot>
</template>

<style lang="scss">
.g-message {
  a {
    text-decoration: underline;
  }
}
</style>
